import { Select, toast, Wordmark } from '@patchbay/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { patchBrandScope } from './api'
import { refreshBrandScopedQueries, rememberBrandScope } from './queries'
import { useBrandScope } from './use-brand-scope'

/** Today's wordmark, plus a brand selector once more than one brand is live (or one is selected, so `All` is reachable). */
export function BrandSwitcher() {
  const scope = useBrandScope()
  if (scope.brands.length <= 1 || !scope.showBrands) return <Wordmark />
  return <BrandSwitcherSelect scope={scope} />
}

function BrandSwitcherSelect({ scope }: { scope: ReturnType<typeof useBrandScope> }) {
  const queryClient = useQueryClient()
  const router = useRouter()
  const save = useMutation({
    mutationFn: (brandScope: string) => patchBrandScope(brandScope),
    onSuccess: (saved, brandScope) => {
      rememberBrandScope(queryClient, brandScope, saved)
      const href = new URL(router.state.location.href, globalThis.location?.origin ?? 'http://patchbay.local')
      href.searchParams.set('brand', brandScope)
      void router.navigate({ href: `${href.pathname}${href.search}${href.hash}` })
      refreshBrandScopedQueries(queryClient)
    },
    onError: (error) => toast(error instanceof Error ? error.message : 'Brand preference could not be saved'),
  })
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Wordmark className="shrink-0" />
      <Select
        aria-label="Brand"
        value={scope.selected}
        disabled={save.isPending}
        options={[
          { value: 'all', label: 'All brands' },
          ...scope.brands.map((brand) => ({ value: brand.slug, label: brand.customer_facing_name })),
        ]}
        onValueChange={(value) => {
          if (value !== scope.selected) save.mutate(value)
        }}
        className="min-w-32 max-w-48"
      />
    </div>
  )
}
